import {
  Button,
  CircularProgress,
  Container,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { AppBar, FilterInput } from 'components';
import NewRiskMatrixModal from 'components/Modal/NewRiskMatrix';
import { useRiskMatrix } from 'contexts/riskMatrix';
import { notification } from 'antd';
import {
  Grid2x2Plus,
  Eye,
  Edit,
  Trash2,
  FileScan,
  Grid2x2X,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { IRiskMatrixViewModel } from 'viewModels';
import { ContentTable, StyledBadge } from './styles';
import {
  GridOff,
  KeyboardArrowDown,
  KeyboardDoubleArrowDown,
  MoreVert,
  Sort,
} from '@mui/icons-material';
import { Cnpj, Cpf } from 'utils/masks';
import moment from 'moment';
import { Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import RiskDetailsModal from 'components/Modal/ShowRisksModal';
import { Columns } from 'props';
import {
  IOrderFieldInputModel,
  IRiskMatrixInputModel,
} from 'inputModels';
import { RiskMatrixController } from 'controllers/RiskMatrixController';

export function RiskMatrixPage() {
  const theme = useTheme();
  const [isModalOpen, setIsOpenModal] = useState(false);
  const [isModalViewRisksOpen, setIsOpenModalViewRisks] =
    useState(false);
  const {
    riskMatrices,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    orderBy,
    setOrder,
    createRiskMatrix,
    updateRiskMatrix,
    deleteRiskMatrix,
    updateRiskMatrixStatus,
  } = useRiskMatrix();
  const [currentMatrix, setCurrentMatrix] =
    useState<IRiskMatrixViewModel>();
  const [selectedMatrix, setSelectedMatrix] =
    useState<IRiskMatrixViewModel | null>(null);

  const handleSort = useCallback(
    (key: Columns) => () => {
      setOrder((prev) => {
        const currentSort = prev.find((o) => o.key === key);
        if (!currentSort) {
          return [
            {
              key,
              direction: 'asc',
            } as IOrderFieldInputModel<Columns>,
          ];
        }
        return [
          {
            key,
            direction:
              currentSort.direction === 'asc' ? 'desc' : 'asc',
          } as IOrderFieldInputModel<Columns>,
        ];
      });
    },
    [setOrder]
  );

  const sortMatrices = useCallback(
    (matrices: IRiskMatrixViewModel[]) => {
      if (!orderBy.length) return matrices;
      const sorted = [...matrices];
      const { key, direction } = orderBy[0];
      return sorted.sort((a, b) => {
        let comparison = 0;
        if (key === 'document') {
          comparison = a.document.localeCompare(b.document);
        } else if (key === 'name') {
          comparison = a.name.localeCompare(b.name);
        } else if (key === 'date') {
          const dateA = moment(a.createdAt).valueOf();
          const dateB = moment(b.createdAt).valueOf();
          comparison = dateA - dateB;
        }
        return direction === 'asc' ? comparison : -comparison;
      });
    },
    [orderBy]
  );

  const handleMenuAction: MenuProps['onClick'] = ({ key }) => {
    if (selectedMatrix) {
      if (key === 'view') {
        setCurrentMatrix(selectedMatrix);
        setIsOpenModalViewRisks(true);
      } else if (key === 'report') {
        RiskMatrixController.exportReport(selectedMatrix.id ?? '');
        notification.success({
          message: 'Relatório da matriz solicitado',
          description: 'O relatorio será baixado autotomaticamente.',
          placement: 'topRight',
          pauseOnHover: true,
          duration: 3,
          showProgress: true,
        });
      } else if (key === 'edit') {
        setCurrentMatrix(selectedMatrix);
        setIsOpenModal(true);
      } else if (key === 'delete') {
        if (selectedMatrix.id) {
          deleteRiskMatrix(selectedMatrix.id);
        }
      } else if (key === 'toggle') {
        if (selectedMatrix.id) {
          updateRiskMatrixStatus(selectedMatrix.id);
        }
      }
    }
    setSelectedMatrix(null);
  };

  const sortedMatrices = sortMatrices(riskMatrices);

  const getMenuItems = (
    matrix: IRiskMatrixViewModel
  ): MenuProps['items'] => [
    {
      key: 'view',
      label: 'Visualizar riscos',
      icon: <Eye size={18} />,
    },
    {
      key: 'edit',
      label: 'Editar matriz',
      icon: <Edit size={18} />,
    },
    {
      key: 'report',
      label: 'Gerar relatório',
      icon: <FileScan size={18} />,
    },
    {
      key: 'toggle',
      label: matrix.disabled
        ? 'Habilitar matriz'
        : 'Desabilitar matriz',
      icon: matrix.disabled ? (
        <Grid2x2Plus size={18} />
      ) : (
        <Grid2x2X size={18} />
      ),
      title: matrix.disabled
        ? 'Habilitar a matriz torna-a visível no dossiê.'
        : 'Desabilitar a matriz torna-a invisível no dossiê.',
      style: { color: matrix.disabled ? '#6df178' : '#faad14' },
    },
    {
      key: 'delete',
      label: 'Excluir',
      icon: <Trash2 size={18} />,
      title: 'Esta ação removerá permanentemente a matriz de risco.',
      danger: true,
    },
  ];

  return (
    <>
      <AppBar />
      <Container
        sx={{ minWidth: '90vw', minHeight: '100vh', pt: 10, pb: 4 }}
      >
        <Paper
          sx={{
            p: 4,
            mb: 3,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          }}
        >
          <Grid2
            container
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              component="p"
              variant="h4"
              color="primary"
              sx={{
                fontWeight: 600,
                position: 'relative',
                marginBottom: '24px',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 60,
                  height: 3,
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: 4,
                },
              }}
            >
              Matrizes de Risco
            </Typography>
            <Button
              onClick={() => {
                setCurrentMatrix(undefined);
                setIsOpenModal(true);
              }}
              variant="contained"
              aria-label="Create new dossier"
              startIcon={<Grid2x2Plus style={{ width: 18 }} />}
            >
              Nova Matriz
            </Button>
          </Grid2>
          <ContentTable>
            <FilterInput onInputChange={() => {}} />
            <Table stickyHeader aria-label="Risk Matrix list table">
              <TableHead>
                <TableRow
                  sx={{
                    th: {
                      backgroundColor: 'white',
                      '& p': {
                        fontWeight: 'bold',
                        color: theme.palette.primary.main,
                      },
                      '& div': {
                        display: 'flex',
                        alignItems: 'center',
                      },
                    },
                  }}
                >
                  <TableCell align="left">
                    <Grid2>
                      <Typography>#</Typography>
                    </Grid2>
                  </TableCell>
                  <TableCell align="left">
                    <Grid2>
                      <Typography>CPF/CNPJ</Typography>
                      <IconButton
                        data-testid="sortIconButton"
                        size="small"
                        onClick={handleSort('document')}
                        aria-label="Sort by CPF/CNPJ"
                      >
                        {orderBy.find((o) => o.key === 'document') ? (
                          <KeyboardArrowDown
                            sx={{
                              transition: 'transform 0.3s ease-in',
                              transform:
                                orderBy.find(
                                  (o) => o.key === 'document'
                                )?.direction === 'asc'
                                  ? 'rotate(-180deg)'
                                  : '',
                            }}
                          />
                        ) : (
                          <Sort />
                        )}
                      </IconButton>
                    </Grid2>
                  </TableCell>
                  <TableCell align="left">
                    <Grid2>
                      <Typography>Nome</Typography>
                      <IconButton
                        data-testid="sortIconButton"
                        size="small"
                        onClick={handleSort('name')}
                        aria-label="Sort by Nome"
                      >
                        {orderBy.find((o) => o.key === 'name') ? (
                          <KeyboardArrowDown
                            sx={{
                              transition: 'transform 0.3s ease-in',
                              transform:
                                orderBy.find((o) => o.key === 'name')
                                  ?.direction === 'asc'
                                  ? 'rotate(-180deg)'
                                  : '',
                            }}
                          />
                        ) : (
                          <Sort />
                        )}
                      </IconButton>
                    </Grid2>
                  </TableCell>
                  <TableCell align="center">
                    <Grid2
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography>Riscos</Typography>
                    </Grid2>
                  </TableCell>
                  <TableCell align="left">
                    <Grid2>
                      <Typography>Criação</Typography>
                      <IconButton
                        data-testid="sortIconButton"
                        size="small"
                        onClick={handleSort('date')}
                        aria-label="Sort by Criação"
                      >
                        {orderBy.find((o) => o.key === 'date') ? (
                          <KeyboardArrowDown
                            sx={{
                              transition: 'transform 0.3s ease-in',
                              transform:
                                orderBy.find((o) => o.key === 'date')
                                  ?.direction === 'asc'
                                  ? 'rotate(-180deg)'
                                  : '',
                            }}
                          />
                        ) : (
                          <Sort />
                        )}
                      </IconButton>
                    </Grid2>
                  </TableCell>
                  <TableCell align="center">
                    <Grid2
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography>Status</Typography>
                      <IconButton
                        data-testid="sortIconButton"
                        size="small"
                        onClick={handleSort('status')}
                        aria-label="Sort by Status"
                      >
                        {orderBy.find((o) => o.key === 'status') ? (
                          <KeyboardArrowDown
                            sx={{
                              transition: 'transform 0.3s ease-in',
                              transform:
                                orderBy.find(
                                  (o) => o.key === 'status'
                                )?.direction === 'asc'
                                  ? 'rotate(-180deg)'
                                  : '',
                            }}
                          />
                        ) : (
                          <Sort />
                        )}
                      </IconButton>
                    </Grid2>
                  </TableCell>
                  <TableCell align="right">
                    <Grid2
                      sx={{
                        display: 'flex',
                        justifyContent: 'right',
                      }}
                    >
                      <Typography>Ações</Typography>
                    </Grid2>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Grid2
                        container
                        alignItems="center"
                        justifyContent="center"
                        sx={{ minHeight: '65px' }}
                      >
                        <CircularProgress size={15} />
                        <Typography
                          variant="caption"
                          color="text"
                          sx={{ fontStyle: 'italic', pl: 1 }}
                        >
                          Carregando lista de matrizes de risco...
                        </Typography>
                      </Grid2>
                    </TableCell>
                  </TableRow>
                ) : !sortedMatrices.length ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Grid2
                        container
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ minHeight: '300px', py: 4 }}
                      >
                        <GridOff
                          sx={{
                            fontSize: 40,
                            color: theme.palette.text.secondary,
                            mb: 2,
                          }}
                          aria-label="No dossiers icon"
                        />
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          sx={{ fontWeight: 500 }}
                        >
                          Nenhuma matriz de risco encontrada
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, fontStyle: 'italic' }}
                        >
                          Crie uma nova matriz de risco para começar!
                        </Typography>
                      </Grid2>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedMatrices.map((matrix, idx) => (
                    <TableRow
                      key={idx}
                      sx={{
                        '&:last-child td, &:last-child th': {
                          border: 0,
                        },
                      }}
                    >
                      <TableCell align="left">
                        <Typography color="primary">
                          {(idx + 1).toString().padStart(3, '0')}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        <Typography>
                          {matrix.document.length === 11
                            ? Cpf(matrix.document)
                            : Cnpj(matrix.document)}
                        </Typography>
                      </TableCell>
                      <TableCell align="left">
                        <Tooltip
                          title={
                            <h1 style={{ fontSize: 15 }}>
                              {matrix.name}
                            </h1>
                          }
                          followCursor
                        >
                          <Typography>{matrix.name}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center">
                        <StyledBadge>
                          <span>
                            {matrix.risks.length === 1
                              ? '1 Risco'
                              : `${matrix.risks.length} Riscos`}
                          </span>
                        </StyledBadge>
                      </TableCell>
                      <TableCell align="left">
                        <Typography>
                          {moment(matrix.createdAt).format(
                            'DD/MM/yyyy HH:mm'
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <StyledBadge
                          style={{
                            backgroundColor: matrix.disabled
                              ? theme.palette.error.main
                              : theme.palette.success.main,
                          }}
                        >
                          <span>
                            {matrix.disabled
                              ? 'Desabilitada'
                              : 'Habilitada'}
                          </span>
                        </StyledBadge>
                      </TableCell>
                      <TableCell align="left">
                        <Grid2
                          sx={{
                            display: 'flex',
                            justifyContent: 'right',
                          }}
                        >
                          <Dropdown
                            menu={{
                              items: getMenuItems(matrix),
                              onClick: handleMenuAction,
                            }}
                            trigger={['click']}
                            placement="bottomRight"
                            arrow
                          >
                            <IconButton
                              sx={{ padding: '2px', margin: 0 }}
                              aria-label="Actions"
                              onClick={() =>
                                setSelectedMatrix(matrix)
                              }
                            >
                              <MoreVert />
                            </IconButton>
                          </Dropdown>
                        </Grid2>
                      </TableCell>
                    </TableRow>
                  ))
                )}
                {sortedMatrices.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      {isFetchingNextPage ? (
                        <Grid2
                          container
                          alignItems="center"
                          justifyContent="center"
                          sx={{ minHeight: '65px' }}
                        >
                          <CircularProgress size={15} />
                          <Typography
                            variant="caption"
                            color="text"
                            sx={{ fontStyle: 'italic', pl: 1 }}
                          >
                            Carregando itens...
                          </Typography>
                        </Grid2>
                      ) : (
                        <Button
                          onClick={() => fetchNextPage()}
                          sx={{ px: 2 }}
                          aria-label="Load more dossiers"
                        >
                          <KeyboardDoubleArrowDown />
                          <Typography
                            paddingLeft={2}
                            variant="button"
                          >
                            Carregar mais...
                          </Typography>
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ContentTable>
        </Paper>
        {isModalOpen && (
          <NewRiskMatrixModal
            open={isModalOpen}
            onClose={() => setIsOpenModal(false)}
            currentMatrix={currentMatrix}
            onSubmit={(data: IRiskMatrixInputModel) => {
              if (currentMatrix?.id) {
                updateRiskMatrix({ ...data, id: currentMatrix.id });
              } else {
                createRiskMatrix(data);
              }
              setIsOpenModal(false);
            }}
          />
        )}
        {isModalViewRisksOpen && currentMatrix && (
          <RiskDetailsModal
            open={isModalViewRisksOpen}
            onClose={() => setIsOpenModalViewRisks(false)}
            currentMatrix={currentMatrix}
          />
        )}
      </Container>
    </>
  );
}
